import { useMemo, useState } from "react";
import { Card, SectionHeader } from "../../components/lib";
import { Segmented, ListRow } from "../../components/lib.overlay";
import { RND_TIMELINE, RND_PRODUCTS, RndTimelineEvent } from "../../mocks/rndData";
import { EmptyState } from "../../components/EmptyState";

const KIND_META: Record<RndTimelineEvent["kind"], { bg: string; fg: string; label: string }> = {
  concept:  { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)",    label: "Concept"  },
  formula:  { bg: "var(--ph-ai-tint)",       fg: "var(--ph-ai)",           label: "Formula"  },
  lab:      { bg: "var(--ph-ai-tint)",       fg: "var(--ph-ai)",           label: "Lab"      },
  sampling: { bg: "var(--ph-warning-tint)",  fg: "var(--ph-warning)",      label: "Sampling" },
  panel:    { bg: "var(--ph-warning-tint)",  fg: "var(--ph-warning)",      label: "Panel"    },
  decision: { bg: "var(--ph-brand-tint)",    fg: "var(--ph-brand-primary)", label: "Decision" },
  launch:   { bg: "var(--ph-success-tint)",  fg: "var(--ph-success)",      label: "Launch"   },
};

export default function Timeline() {
  // Guard against empty RND_PRODUCTS (post-purge, live workspace pre-first-launch).
  const hasProducts = Array.isArray(RND_PRODUCTS) && RND_PRODUCTS.length > 0;
  const [productId, setProductId] = useState<string>(hasProducts ? RND_PRODUCTS[0].id : "");
  const events = useMemo(
    () => (productId ? RND_TIMELINE.filter((t) => t.productId === productId) : []),
    [productId],
  );
  const product = hasProducts
    ? (RND_PRODUCTS.find((p) => p.id === productId) || RND_PRODUCTS[0])
    : null;

  if (!hasProducts || !product) {
    return (
      <div className="space-y-6" data-testid="rd-timeline">
        <Card testId="rd-tl-empty">
          <SectionHeader eyebrow="Timeline" title="Choose a product" />
          <EmptyState
            testId="rd-timeline-empty"
            title="No R&D products yet."
            hint="Register a product in Product Master to start tracking its formulation, sampling, panel, decision and launch events on the timeline."
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rd-timeline">
      <Card testId="rd-tl-picker">
        <SectionHeader eyebrow="Timeline" title="Choose a product" />
        <Segmented<string>
          testId="rd-tl-seg"
          value={productId}
          onChange={setProductId}
          options={RND_PRODUCTS.map((p) => ({ value: p.id, label: p.name }))}
        />
      </Card>

      <Card padded={false} testId="rd-tl-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow={product.codename} title={`${product.name} · ${events.length} event${events.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2">
          {events.length === 0 && <div className="p-4 text-sm" style={{ color: "var(--ph-ink-muted)" }}>No events yet.</div>}
          {events.map((e) => {
            const m = KIND_META[e.kind];
            return (
              <ListRow key={e.id} testId={`rd-tl-${e.id}`}
                label={<span className="inline-flex items-center gap-2">{e.title}
                  <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: m.bg, color: m.fg, borderRadius: "var(--ph-radius-sm)" }}>{m.label}</span>
                </span>}
                sublabel={<>{e.dateRelative} — {e.detail}</>}
              />
            );
          })}
        </div>
      </Card>
    </div>
  );
}
